import i18next from 'i18next';
import {ofType} from 'redux-observable';
import {of, from} from 'rxjs';
import {mergeMap, map, catchError, flatMap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {fetchLocation} from 'web-app/services/location';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as SignInAPI from 'web-app/react/modals-container/child-pickup-modal/api';
import EmployeeAPI from 'web-app/api/employee-api';
import {track} from 'web-app/util/famlytics';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as AccountSelectors from 'web-app/react/session/account/selectors';

import * as API from './api';
import * as Actions from './actions';
import * as Selectors from './selectors';
import {checkingInOutPersonType} from './helpers';

const fetchPossibilitiesFromLocationEpic = action$ =>
    action$.pipe(
        ofType(Actions.fetchPossibilitiesFromLocation.type),
        mergeMap(() =>
            from(fetchLocation()).pipe(
                mergeMap(({coords}) => {
                    return API.getPossibilitiesFromLocation(coords.latitude, coords.longitude).pipe(
                        map(response => Actions.fetchPossibilitiesFromLocationSuccess.action(response)),
                        catchError(err => of(Actions.fetchPossibilitiesFromLocationFailed.action(err))),
                    );
                }),
                catchError(err => of(Actions.fetchPossibilitiesFromLocationFailed.action(err))),
            ),
        ),
    );

const signInEmployee = action$ =>
    action$.pipe(
        ofType(Actions.signInEmployee.type),
        flatMap(({payload}: ReturnType<typeof Actions.signInEmployee.action>) => {
            return from(
                EmployeeAPI.checkin(payload.employeeId, payload.group.groupId, payload.statementIds, true),
            ).pipe(
                flatMap(response => {
                    track(TrackingEvents.SIGN_IN_OUT_STAFF_MEMBER_CHECKED_IN, {
                        openedFrom: 'GPS sign in',
                    });
                    return [
                        Actions.signInEmployeeSuccess.action(payload.employeeId, payload.group, response),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('checkin.isNowCheckedIn', {name: payload.name}),
                        ),
                        Actions.hide.action(),
                    ];
                }),
                catchError(() => of(Actions.signInEmployeeFailed.action(payload.employeeId))),
            );
        }),
    );

const signoutEmployee = action$ =>
    action$.pipe(
        ofType(Actions.signOutEmployee.type),
        flatMap(({payload}: ReturnType<typeof Actions.signOutEmployee.action>) => {
            return from(EmployeeAPI.checkout(payload.employeeId)).pipe(
                flatMap(response => {
                    track(TrackingEvents.SIGN_IN_OUT_STAFF_MEMBER_CHECKED_OUT, {
                        openedFrom: 'GPS sign in',
                    });
                    return [
                        Actions.signOutEmployeeSuccess.action(payload.employeeId, response),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('checkin.isNowCheckedOut', {name: payload.name}),
                        ),
                        Actions.hide.action(),
                    ];
                }),
                catchError(() => of(Actions.signOutEmployeeFailed.action(payload.employeeId))),
            );
        }),
    );

const updateChildSignIn = (action$, state$) =>
    action$.pipe(
        ofType(Actions.updateSignIn.type),
        mergeMap(({payload}: ReturnType<typeof Actions.updateSignIn.action>) => {
            const {childId, pickupTime, relationId, statementIds} = payload.signIn;
            const canSignIn = Selectors.makeGetCanSignIn()(state$.value, {childId});

            if (!canSignIn) {
                const pickupFulfilled = Selectors.makePickupFulfilled()(state$.value, {childId});
                if (!pickupFulfilled) {
                    return [
                        GlobalEventActions.updateError.action(i18next.t('checkin.selectTimeAndPickup')),
                        Actions.updateSignInFailed.action(new Error('Pickup person and time are mandatory'), childId),
                    ];
                }
                const statementsFulfilled = Selectors.makeStatementsAreFulfilled()(state$.value, {childId});
                if (!statementsFulfilled) {
                    return [
                        GlobalEventActions.updateError.action(i18next.t('checkin.checkStatements')),
                        Actions.updateSignInFailed.action(new Error('Statements needs to be checked'), childId),
                    ];
                }
            }

            return SignInAPI.signInObservable(childId, pickupTime, relationId, undefined, statementIds).pipe(
                flatMap(response => {
                    const childSignIns = Selectors.childSignIns(state$.value);
                    const rolesIds = AccountSelectors.getRoles2(state$.value).map(role => role.targetId);
                    const childName = Selectors.makeGetChildName()(state$.value, {childId});

                    track(TrackingEvents.SIGN_IN_OUT_CHILD_CHECKED_IN, {
                        openedFrom: 'GPS sign in',
                        pickupTimeSelected: hasValue(pickupTime),
                        pickupPersonSelected: hasValue(response.pickupRelationId),
                        checkingInOutPersonType: checkingInOutPersonType(childId, rolesIds),
                    });
                    track(TrackingEvents.CHILD_ATTENDANCE_LOGS, {openedFrom: 'GPS sign in'});

                    const successActionsBase = [
                        Actions.updateSignInSuccess.action(response),
                        GlobalEventActions.updateSuccess.action(i18next.t('checkin.isNowCheckedIn', {name: childName})),
                    ];
                    const successActions =
                        childSignIns && childSignIns.size && childSignIns.size > 1
                            ? successActionsBase
                            : [...successActionsBase, Actions.hide.action()];

                    return successActions;
                }),
                catchError(e => of(Actions.updateSignInFailed.action(e, childId))),
            );
        }),
    );

const childSignOut = (action$, state$) =>
    action$.pipe(
        ofType(Actions.signOut.type),
        mergeMap(({payload}: ReturnType<typeof Actions.signOut.action>) => {
            return API.signOut(payload.childId).pipe(
                flatMap(response => {
                    const childSignIns = Selectors.childSignIns(state$.value);
                    const childName = Selectors.makeGetChildName()(state$.value, {childId: payload.childId});
                    const rolesIds = AccountSelectors.getRoles2(state$.value).map(role => role.targetId);

                    track(TrackingEvents.SIGN_IN_OUT_CHILD_CHECKED_OUT, {
                        openedFrom: 'GPS sign out',
                        checkingInOutPersonType: checkingInOutPersonType(payload.childId, rolesIds),
                    });
                    track(TrackingEvents.CHILD_ATTENDANCE_LOGS, {openedFrom: 'GPS sign out'});

                    const successActionsBase = [
                        Actions.signOutSuccess.action(response),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('checkin.isNowCheckedOut', {name: childName}),
                        ),
                    ];
                    const successActions =
                        childSignIns && childSignIns.size && childSignIns.size > 1
                            ? successActionsBase
                            : [...successActionsBase, Actions.hide.action()];

                    return successActions;
                }),
                catchError(e => of(Actions.signOutFailed.action(e, payload.childId))),
            );
        }),
    );

export default combineEpics(
    fetchPossibilitiesFromLocationEpic,
    updateChildSignIn,
    childSignOut,
    signInEmployee,
    signoutEmployee,
);
